/**
 * ShortUrlRedirect Component
 *
 * Resolves a short URL code via the backend API and redirects
 * to the target survey page. This allows short URLs to use the
 * same frontend domain (e.g. demo.surveytip.com/s/Ab3xK7m)
 * without needing a separate short domain.
 */

import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from './api/client'

export default function ShortUrlRedirect() {
  const { code } = useParams()
  const navigate = useNavigate()
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!code) return

    let cancelled = false

    ;(async () => {
      try {
        // Call the backend API to resolve the short code to a target path
        const data = await api.get(`/short-urls/resolve/${code}`)

        if (cancelled) return

        if (data?.target_url) {
          // target_url is a path like /run/{survey_id} — navigate client-side
          navigate(data.target_url, { replace: true })
        } else {
          setError('This short link is not valid or has expired.')
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Short URL resolve failed:', err)
          if (err.status === 404) {
            setError('This short link is not valid or has expired.')
          } else {
            setError('Unable to resolve this link. Please check the URL and try again.')
          }
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [code, navigate])

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem 1.5rem' }}>
        <div
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 1.25rem',
            borderRadius: '50%',
            background: '#fff5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.75rem',
          }}>
          🔗
        </div>
        <h2 style={{ margin: '0 0 0.5rem', color: '#2d3748', fontSize: '1.5rem' }}>
          Link Not Found
        </h2>
        <p
          style={{
            color: '#718096',
            maxWidth: 420,
            margin: '0 auto 1.5rem',
            lineHeight: 1.6,
          }}>
          {error}
        </p>
        <a
          href="/"
          style={{
            display: 'inline-block',
            padding: '0.75rem 1.5rem',
            background: '#0D9488',
            color: 'white',
            borderRadius: '10px',
            textDecoration: 'none',
            fontWeight: 600,
          }}>
          Go to Home
        </a>
      </div>
    )
  }

  // Loading state while resolving
  return (
    <div style={{ textAlign: 'center', padding: '4rem 1.5rem' }}>
      <div
        style={{
          width: 40,
          height: 40,
          margin: '0 auto 1rem',
          border: '3px solid #e2e8f0',
          borderTopColor: '#0D9488',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <p style={{ color: '#718096' }}>Redirecting...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
